import { Alignment } from "@blueprintjs/core";
import type { IconName } from "@blueprintjs/icons";
import type { ButtonPlacement, ButtonVariant, RecaptchaType } from "components/constants";
import { ButtonPlacementTypes, ButtonVariantTypes, RecaptchaTypes } from "components/constants";
import { EventType } from "constants/AppsmithActionConstants/ActionConstants";
import type { WidgetType } from "constants/WidgetConstants";
import { ValidationTypes } from "constants/WidgetValidation";
import type { Stylesheet } from "entities/AppTheming";
import React from "react";
import type { WidgetProps, WidgetState } from "widgets/BaseWidget";
import BaseWidget from "widgets/BaseWidget";
import ButtonComponent from "../component";
import { DefaultAutocompleteDefinitions } from "widgets/WidgetUtils";
import type { AutocompletionDefinitions } from "widgets/constants";
import type { WidgetProperties } from "../../../selectors/propertyPaneSelectors";
import General, { GeneralEvent, GeneralStyle } from "../../TableWidget/widget/propertyConfig/PanelConfig/General";

class ButtonGroupWidget extends BaseWidget<ButtonGroupProps, ButtonGroupState> {
  constructor(props: ButtonGroupProps) {
    super(props);
  }

  static getAutocompleteDefinitions(): AutocompletionDefinitions {
    return {
      "!doc":
        "Buttons are used to capture user intent and trigger actions based on that intent",
      "!url": "https://docs.appsmith.com/widget-reference/button",
      isVisible: DefaultAutocompleteDefinitions.isVisible,
      text: "string",
      isDisabled: "bool"
    };
  }
  static getPropertyPaneEventConfig(){
    return super.getWidgetEvents('ButtonGroup');
  }
  static getPropertyPaneContentConfig() {
    return [
      {
        sectionName: "一般",
        children: [
          {
            propertyName: "text",
            label: "标签文字",
            helpText: "Sets the label of the button",
            controlType: "INPUT_TEXT",
            placeholderText: "确定",
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "data",
            isJSConvertible: false,
            label: "Items",
            helpText: "Data",
            prefix: "",
            controlType: "TABLE_COLUMNS",
            isBindProperty: false,
            isTriggerProperty: false,
            updateHook: (props: any, propertyPath: string, propertyValue: any) => {
              const newData = Object.fromEntries(
                Object.entries(propertyValue).map(([key, val]:[string, any]) => [key, {
                  id: val.id,
                  label: val.label,
                  iconName: val.iconName||'',
                  onClick: val.onClick
                }])
              );
              return [
                {
                  propertyPath,
                  propertyValue: newData
                },
              ];
            },
            panelConfig: {
              editableTitle: true,
              titlePropertyName: "label",
              panelIdPropertyName: "id",
              contentChildren: [
                {
                  sectionName: "一般",
                  children: [
                    {
                      propertyName: "iconName",
                      label: "图标",
                      helpText: "Sets the icon to be used for the icon button",
                      controlType: "ICON_SELECT",
                      defaultIconName: "plus",
                      isJSConvertible: false,
                      isBindProperty: true,
                      isTriggerProperty: false,
                      validation: {
                        type: ValidationTypes.TEXT,
                        params: {},
                      },
                    },
                    {
                      propertyName: "isFocus",
                      label: "是否选中",
                      helpText: "Focus input to this widget",
                      controlType: "SWITCH",
                      isJSConvertible: true,
                      isBindProperty: true,
                      isTriggerProperty: false,
                      validation: { type: ValidationTypes.BOOLEAN },
                      dependencies: ["data"],
                      updateHook: (props: any, propertyPath: string, propertyValue: any)=>{
                        const parts = propertyPath.split(".");
                        const currentKey = parts[1];
                        const data = props.data || {};
                        const newData = Object.fromEntries(
                          Object.entries(data).map(([key, val]: [string, any]) => [
                            key,
                            {
                              ...val,
                              isFocus: key === currentKey ? propertyValue : false,
                            },
                          ])
                        );
                        return [
                          {
                            propertyPath: "data",
                            propertyValue: newData,
                          },
                        ];
                      }
                    },

                  ],
                }
              ],
              styleChildren: [],
              eventChildren: [
                {
                  "sectionName": "Events",
                  "children": [
                    {
                      "helpText": "when the button is onRowClick",
                      "propertyName": "onClick",
                      "label": "onClick",
                      "controlType": "ACTION_SELECTOR",
                      "isJSConvertible": true,
                      "isBindProperty": true,
                      "isTriggerProperty": true
                    }
                  ]
                }
              ]
            },
          }
        ],
      },
      {
        sectionName: "设置",
        children: [
          {
            propertyName: "isDisabled",
            label: "是否禁用",
            helpText: "Disables input to this widget",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.BOOLEAN },
          },
          {
            propertyName: "isVisible",
            label: "是否可见",
            helpText: "Controls the visibility of the widget",
            controlType: "SWITCH",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.BOOLEAN,
            },
          }
        ],
      },
    ];
  }

  static getPropertyPaneStyleConfig() {
    return [
      {
        sectionName: "按钮类型",
        children: [
          {
            propertyName: "buttonVariant",
            label: "类型",
            controlType: "ICON_TABS",
            fullWidth: true,
            helpText: "Sets the variant of the icon button",
            options: [
              {
                label: "主要按钮",
                value: ButtonVariantTypes.PRIMARY,
              },
              {
                label: "次要按钮",
                value: ButtonVariantTypes.SECONDARY,
              },
              {
                label: "文字按钮",
                value: ButtonVariantTypes.TERTIARY,
              },
            ],
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                allowedValues: [
                  ButtonVariantTypes.PRIMARY,
                  ButtonVariantTypes.SECONDARY,
                  ButtonVariantTypes.TERTIARY,
                ],
                default: ButtonVariantTypes.PRIMARY,
              },
            },
          },
          {
            propertyName: "itemSpacing",
            helpText: "Sets the spacing between items in pixels to a max 16 px",
            placeholderText: "0",
            label: "间距",
            controlType: "INPUT_TEXT",
            isBindProperty: true,
            isTriggerProperty: false,
            inputType: "INTEGER",
            validation: {
              type: ValidationTypes.NUMBER,
              params: { min: 0, max: 16 },
            },
          }
        ],
      },
      {
        sectionName: "按钮样式",
        children: [
          {
            propertyName: "buttonColor",
            helpText: "Changes the color of the button",
            label: "按钮颜色",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "textColor",
            label: "字体颜色",
            helpText: "Control the color of the label associated",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^(?![<|{{]).+/,
              },
            },
          },
          {
            propertyName: "fontSize",
            label: "字体大小",
            helpText: "Control the font size of the label associated",
            controlType: "DROP_DOWN",
            defaultValue: "0.875rem",
            options: [
              {
                label: "S",
                value: "0.875rem",
                subText: "0.875rem",
              },
              {
                label: "M",
                value: "1rem",
                subText: "1rem",
              },
              {
                label: "L",
                value: "1.25rem",
                subText: "1.25rem",
              },
              {
                label: "XL",
                value: "1.875rem",
                subText: "1.875rem",
              },
              {
                label: "XXL",
                value: "3rem",
                subText: "3rem",
              },
              {
                label: "3XL",
                value: "3.75rem",
                subText: "3.75rem",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "fontStyle",
            label: "加粗&斜体",
            helpText: "Control if the label should be bold or italics",
            controlType: "BUTTON_GROUP",
            options: [
              {
                icon: "text-bold",
                value: "BOLD",
              },
              {
                icon: "text-italic",
                value: "ITALIC",
              },
            ],
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          }
        ],
      },
      {
        sectionName: "选中按钮样式",
        children: [
          {
            propertyName: "buttonColorFocus",
            helpText: "Changes the color of the button",
            label: "按钮颜色",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
          {
            propertyName: "textColorFocus",
            label: "字体颜色",
            helpText: "Control the color of the label associated",
            controlType: "COLOR_PICKER",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT,
              params: {
                regex: /^(?![<|{{]).+/,
              },
            },
          }
        ],
      },
      {
        sectionName: "图标",
        children: [
          {
            propertyName: "iconAlign",
            label: "位置",
            helpText: "Sets the icon alignment of the button",
            controlType: "ICON_TABS",
            fullWidth: false,
            options: [
              {
                label: "左边",
                value: "row",
              },
              {
                label: "右边",
                value: "row-reverse",
              },
              {
                label: "上边",
                value: "column",
              },
              {
                label: "下边",
                value: "column-reverse",
              },
            ],
            isBindProperty: false,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT
            },
          },
          {
            propertyName: "placement",
            label: "对齐方式",
            controlType: "ICON_TABS",
            fullWidth: true,
            helpText: "Sets the space between items",
            options: [
              {
                label: "居左对齐",
                value: "left",
              },
              {
                label: "两边对齐",
                value: 'space-between',
              },
              {
                label: "居中对齐",
                value: 'center',
              },
            ],
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {
              type: ValidationTypes.TEXT
            },
          },
        ],
      },
      {
        sectionName: "圆角&阴影",
        children: [
          {
            propertyName: "borderRadius",
            label: "边框圆角",
            helpText:
              "Rounds the corners of the icon button's outer border edge",
            controlType: "BORDER_RADIUS_OPTIONS",
            isJSConvertible: true,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: {type: ValidationTypes.TEXT},
          },
          {
            propertyName: "boxShadow",
            label: "阴影",
            helpText:
              "Enables you to cast a drop shadow from the frame of the widget",
            controlType: "BOX_SHADOW_OPTIONS",
            isJSConvertible: false,
            isBindProperty: true,
            isTriggerProperty: false,
            validation: { type: ValidationTypes.TEXT },
          },
        ]
      }
    ];
  }

  static getStylesheetConfig(): Stylesheet {
    return {
      buttonColorFocus: "{{appsmith.theme.colors.primaryColor}}",
      borderRadius: "{{appsmith.theme.borderRadius.appBorderRadius}}",
      boxShadow: "{{appsmith.theme.boxShadow.appBoxShadow}}",
    };
  }

  onItemClick = (event:any)=> {
    super.executeAction({
      triggerPropertyName: "onClick",
      dynamicString: event,
      event: {
        type: EventType.ON_CLICK
      },
    });
  }
  getPageView() {
    return (
      <ButtonComponent
        {...this.props}
        onItemClick={this.onItemClick}
      />
    );
  }

  static getWidgetType(): WidgetType {
    return "BUTTON_GROUP_WIDGET";
  }
}

export interface ButtonGroupProps extends WidgetProps {
  text?: string;
  onClick?: string;
  isDisabled?: boolean;
  isVisible?: boolean;
  recaptchaType?: RecaptchaType;
  googleRecaptchaKey?: string;
  buttonVariant?: ButtonVariant;
  buttonColor?: string;
  borderRadius?: string;
  boxShadow?: string;
  iconName?: IconName;
  iconAlign?: Alignment;
  placement?: ButtonPlacement;
  disabledWhenInvalid?: boolean;
  resetFormOnClick?: boolean;
  width?:string;
  height?:string;
}

interface ButtonGroupState extends WidgetState {
  isLoading: boolean;
}

export default ButtonGroupWidget;
